import React from 'react';
import { Table, Group, Text } from '@mantine/core';
import { ProjectTableProps } from '../types/dashboard.types';
import { PlayIcon } from '../../../modules/audio_player/audio_player_module';
import { isAudioFile } from '../../../utils/file_utils';
import { formatTimeAgo, formatDuration } from '../utils/timeFormatting';
import {useIsMobile} from '@/utils/user_agent';

export const ProjectTable: React.FC<ProjectTableProps> = ({
  projects,
  onProjectClick,
  showLoadMore,
  onLoadMore,
  projectFiles,
  attachments,
  projectRevisions
}) => {

  const isMobile = useIsMobile();

  const rows = projects.map((project) => {
    // Find files attached to this project
    const projectFileIds = attachments
      ?.filter(a => a.parent_type === 'Project' && a.parent_id === project.id && a.child_type === 'File')
      .map(a => a.child_id) || [];

    const projectAudioFiles = projectFiles?.filter(f =>
      projectFileIds.includes(f.id) && isAudioFile(f.name)
    ) || [];

    // If we have a bounce_file attribute, use that
    let mainAudioFile = projectAudioFiles[0];
    if (project.attributes?.bounce_file) {
      const bounceFile = projectFiles?.find(f =>
        f.relative_path === project.attributes.bounce_file || f.name === project.attributes.bounce_file
      );
      if (bounceFile && isAudioFile(bounceFile.name)) {
        mainAudioFile = bounceFile;
      }
    }

    const fileDuration = mainAudioFile ? mainAudioFile.attributes?.duration : project.duration;

    // Find the most recent project revision for this project
    const projectRevisionList = projectRevisions?.filter(r => r.project_id === project.id) || [];
    const mostRecentRevision = projectRevisionList.sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
    const lastActivityTime = mostRecentRevision?.created_at || project.updated_at || project.created_at;

    return (
    <Table.Tr key={project.id} style={{ cursor: 'pointer' }} onClick={() => onProjectClick(project)}>
      <Table.Td style={{ width: '80px', padding: isMobile ? '6px' : '8px' }}>
        <Group gap="xs" wrap="nowrap" align="center">
          {mainAudioFile ? (
            <PlayIcon fileInfo={mainAudioFile} size={16} />
          ) : (
            <div style={{ width: 16, height: 16 }} />
          )}
          {fileDuration && (
            <Text size="sm" ml="xs">
              {formatDuration(typeof fileDuration === 'string' ? parseInt(fileDuration) : fileDuration)}
            </Text>
          )}
        </Group>
      </Table.Td>
      <Table.Td style={{ textAlign: 'left', padding: isMobile ? '6px' : '8px' }}>
        <Group gap="xs" align="center">
          <Text size="sm">
            {project.name}
          </Text>
        </Group>
      </Table.Td>
      {!isMobile && (
        <>
          <Table.Td style={{ textAlign: 'right', padding: '8px' }}>
            <Text size="sm" c="dimmed">
              {formatTimeAgo(project.created_at, isMobile)}
            </Text>
          </Table.Td>
        </>
      )}
      <Table.Td style={{ textAlign: 'right', padding: isMobile ? '6px' : '8px' }}>
        <Text size="sm" c="dimmed">
          {formatTimeAgo(lastActivityTime, isMobile)}
        </Text>
      </Table.Td>
    </Table.Tr>
    );
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table striped highlightOnHover withRowBorders>
        {!isMobile && (
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: '80px', padding: '8px' }}></Table.Th>
              <Table.Th style={{ minWidth: '150px', textAlign: 'left' }}></Table.Th>
              <Table.Th style={{ width: '140px', textAlign: 'right' }}>Created</Table.Th>
              <Table.Th style={{ width: '140px', textAlign: 'right' }}>Last Updated</Table.Th>
            </Table.Tr>
          </Table.Thead>
        )}
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
      {showLoadMore && onLoadMore && (
        <Group justify="center" mt="md">
          <Text
            size="sm"
            c="blue"
            style={{ cursor: 'pointer' }}
            onClick={onLoadMore}
          >
            Show More...
          </Text>
        </Group>
      )}
    </div>
  );
};
